import React from "react";
import { NonDeletedSchemadrawElement } from "../../element/types";
import { t } from "../../utils/i18n";

import { actionSaveFileToDisk } from "../../actions/actionExport";
import { AppState, BinaryFiles, ExportOpts } from "../../utils/types";
import { Card } from "./Card";
import { Dialog } from "./Dialog";
import { exportToFileIcon, LinkIcon, exportToCloudIcon } from "./icons";
import { ToolButton } from "./ToolButton";

import { ActionManager } from "../../actions/manager";
import { nativeFileSystemSupported } from "../../data/filesystem";
import { trackEvent } from "../../utils/analytics";
import { getFrame } from "../../utils/utils";
import "./ExportDialog.scss";

export type ExportCB = (
  elements: readonly NonDeletedSchemadrawElement[],
  scale?: number,
) => void;

const JSONExportModal = ({
  elements,
  appState,
  files,
  actionManager,
  exportOpts,
  canvas,
}: {
  appState: AppState;
  files: BinaryFiles;
  elements: readonly NonDeletedSchemadrawElement[];
  actionManager: ActionManager;
  onCloseRequest: () => void;
  exportOpts: ExportOpts;
  canvas: HTMLCanvasElement;
}) => {
  const { onExportToBackend, onShareToBackend } = exportOpts;
  return (
    <div className="ExportDialog ExportDialog--json">
      <div className="Card-details">
        {actionManager.renderAction("changeProjectName")}
      </div>
      <div className="ExportDialog-cards">
        {exportOpts.saveFileToDisk && (
          <Card color="lime">
            <div className="Card-icon">{exportToFileIcon}</div>
            <h2>{t("exportDialog.disk_title")}</h2>
            <div className="Card-details">
              {t("exportDialog.disk_details")}
            </div>
            <ToolButton
              className="Card-button"
              type="button"
              title={t("exportDialog.disk_button")}
              aria-label={t("exportDialog.disk_button")}
              showAriaLabel={true}
              onClick={() => {
                actionManager.executeAction(actionSaveFileToDisk, "ui");
              }}
            />
          </Card>
        )}
        {onExportToBackend && (
          <Card color="blue">
            <div className="Card-icon">{exportToCloudIcon}</div>
            <h2>{t("exportDialog.cloud_title")}</h2>
            <div className="Card-details">
              {t("exportDialog.cloud_details")}
            </div>
            <ToolButton
              className="Card-button"
              type="button"
              title={t("exportDialog.cloud_button")}
              aria-label={t("exportDialog.cloud_button")}
              showAriaLabel={true}
              onClick={() => {
                onExportToBackend(elements, appState, files);
              }}
            />
          </Card>
        )}
        {onShareToBackend && (
          <Card color="pink">
            <div className="Card-icon">{LinkIcon}</div>
            <h2>{t("exportDialog.link_title")}</h2>
            <div className="Card-details">{t("exportDialog.link_details")}</div>
            <ToolButton
              className="Card-button"
              type="button"
              title={t("exportDialog.link_button")}
              aria-label={t("exportDialog.link_button")}
              showAriaLabel={true}
              onClick={() => {
                onShareToBackend(elements, appState, files, canvas);
                trackEvent("export", "link", `ui (${getFrame()})`);
              }}
            />
          </Card>
        )}
        {exportOpts.renderCustomUI &&
          exportOpts.renderCustomUI(elements, appState, files, canvas)}
      </div>
    </div>
  );
};

export const JSONExportDialog = ({
  elements,
  appState,
  files,
  actionManager,
  exportOpts,
  canvas,
  setAppState,
}: {
  elements: readonly NonDeletedSchemadrawElement[];
  appState: AppState;
  files: BinaryFiles;
  actionManager: ActionManager;
  exportOpts: ExportOpts;
  canvas: HTMLCanvasElement;
  setAppState: React.Component<any, AppState>["setState"];
}) => {
  const handleClose = React.useCallback(() => {
    setAppState({ openDialog: null });
  }, [setAppState]);

  return (
    <>
      {appState.openDialog?.name === "jsonExport" && (
        <Dialog onCloseRequest={handleClose} title={t("buttons.export")}>
          <JSONExportModal
            elements={elements}
            appState={appState}
            files={files}
            actionManager={actionManager}
            onCloseRequest={handleClose}
            exportOpts={exportOpts}
            canvas={canvas}
          />
        </Dialog>
      )}
    </>
  );
};
